import type { Aspiration } from '../../../types/aspirations';
import type { Goal } from '../../../types/goals';
import type { List } from '../../../types/lists';

export const aspirations: Aspiration[] = [
  {
    id: 'aspiration_financial_independence',
    title: 'Be financially independent',
    description: 'Become financially independent by generating sustainable revenue from my work and investments.',
    category: 'Personal',
    priority: 5,
    status: 'completed',
    progress: 100,
    deadline: new Date('2030-12-31'),
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-11-15'),
    linkedGoals: [
      { id: 'goal_passive_income', title: 'Build Passive Income Streams', completed: false, deadline: new Date('2026-12-31') },
      { id: 'goal_investment_portfolio', title: 'Diversify Investment Portfolio', completed: false, deadline: new Date('2025-12-31') },
      { id: 'goal_emergency_fund', title: 'Build 12-Month Emergency Fund', completed: false, deadline: new Date('2025-06-30') }
    ]
  }
];

export const goals: Goal[] = [
  {
    id: 'goal_passive_income',
    title: 'Build Passive Income Streams',
    description: 'Create multiple streams of passive income to reduce dependence on traditional employment.',
    priority: 1,
    status: 'completed',
    progress: 100,
    smartCriteria: {
      specific: 'Generate $3000/month in passive income from 3 different sources',
      measurable: 'Track monthly income from investments, real estate, and digital products',
      achievable: 'Start with index funds, explore real estate, create digital products',
      relevant: 'Essential for financial independence',
      timeBound: 'Achieve by December 2026'
    },
    deadline: new Date('2026-12-31'),
    startDate: new Date('2024-11-01'),
    createdAt: new Date('2024-10-15'),
    updatedAt: new Date('2024-11-15'),
    aspirationId: 'aspiration_financial_independence',
    lists: [
      {
        id: 'list_passive_1',
        title: 'Investment Portfolio',
        description: 'Build diversified investment portfolio',
        deadline: new Date('2025-06-30'),
        completed: true
      },
      {
        id: 'list_passive_2',
        title: 'Digital Products',
        description: 'Create and launch digital products/courses',
        deadline: new Date('2026-06-30'),
        completed: true
      }
    ],
    successMetrics: ['Monthly passive income', 'Portfolio diversity', 'Income consistency'],
    resources: ['Investment broker', 'Financial advisor', 'Online course platforms'],
  },
  {
    id: 'goal_emergency_fund',
    title: 'Build 12-Month Emergency Fund',
    description: 'Save enough money to cover 12 months of expenses for complete financial security.',
    priority: 2,
    status: 'completed',
    progress: 100,
    smartCriteria: {
      specific: 'Save $60,000 in high-yield savings account',
      measurable: 'Track monthly savings progress towards target',
      achievable: 'Save $2000 per month consistently',
      relevant: 'Provides financial security and independence',
      timeBound: 'Complete by June 2025'
    },
    deadline: new Date('2025-06-30'),
    startDate: new Date('2024-06-01'),
    createdAt: new Date('2024-05-15'),
    updatedAt: new Date('2024-11-15'),
    aspirationId: 'aspiration_financial_independence',
    lists: [
      {
        id: 'list_emergency_1',
        title: 'Budget Optimization',
        description: 'Reduce expenses and increase savings rate',
        deadline: new Date('2024-12-31'),
        completed: true
      },
      {
        id: 'list_emergency_2',
        title: 'Savings Automation',
        description: 'Automate savings transfers',
        deadline: new Date('2025-01-31'),
        completed: true
      }
    ],
    successMetrics: ['Monthly savings amount', 'Total fund balance', 'Expense reduction'],
    resources: ['High-yield savings account', 'Budgeting app', 'Financial tracking tools'],
  },
  {
    id: 'goal_side_income',
    title: 'Develop Side Income Stream',
    description: 'Create additional income through SaaS development and digital products.',
    priority: 2,
    status: 'completed',
    progress: 100,
    smartCriteria: {
      specific: 'Launch SaaS product generating $2000/month recurring revenue',
      measurable: 'Track MRR, user acquisition, and customer retention',
      achievable: 'Build on existing tech skills with 15 hours/week commitment',
      relevant: 'Supports financial independence aspiration',
      timeBound: 'Achieve $2000 MRR by December 2025'
    },
    deadline: new Date('2025-12-31'),
    startDate: new Date('2024-11-01'),
    createdAt: new Date('2024-10-20'),
    updatedAt: new Date('2024-11-20'),
    aspirationId: 'aspiration_financial_independence',
    lists: [
      {
        id: 'list_saas_mvp',
        title: 'SaaS MVP Development',
        description: 'Core features for developer productivity tool',
        deadline: new Date('2025-03-31'),
        completed: true
      }
    ],
    successMetrics: ['Monthly recurring revenue', 'User growth', 'Customer satisfaction'],
    resources: ['Development tools', 'Cloud infrastructure', 'Marketing budget', 'User feedback'],
  }
];

export const lists: List[] = [
  // Financial Independence Tasks
  {
    id: 'list_financial_independence',
    title: '💰 Financial Independence - Q4 2024',
    description: 'Tasks to build passive income and emergency fund',
    status: 'complete',
    priority: 2,
    goalId: 'goal_passive_income',
    deadline: new Date('2024-12-31'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 20,
    actualHours: 6,
    items: [
      {
        id: 'item_budget_1',
        content: 'Complete monthly budget review',
        description: 'Track all expenses and identify savings opportunities',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-05'),
        estimatedMinutes: 60,
        actualMinutes: 45,
        category: 'financial',
        history: [{ id: 'history_item_budget_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_emergency_1',
        content: 'Transfer $2000 to emergency fund',
        description: 'Monthly contribution to reach 12-month goal',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 10,
        actualMinutes: 5,
        category: 'financial',
        history: [{ id: 'history_item_emergency_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_invest_1',
        content: 'Research dividend growth stocks',
        description: 'Identify 5 potential dividend stocks for portfolio',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 180,
        dueDate: new Date('2024-11-25'),
        category: 'financial',
        history: [{ id: 'history_item_invest_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_invest_2',
        content: 'Max out 401k contribution',
        description: 'Increase contribution to hit annual limit',
        priority: 4,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 30,
        dueDate: new Date('2024-11-30'),
        category: 'financial',
        history: [{ id: 'history_item_invest_2_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_side_1',
        content: 'Research potential side business ideas',
        description: 'Brainstorm 10 potential passive income ideas',
        priority: 5,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 120,
        dueDate: new Date('2024-12-05'),
        category: 'financial',
        history: [{ id: 'history_item_side_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_real_estate_1',
        content: 'Research REIT investments',
        description: 'Compare different REIT options for portfolio',
        priority: 6,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 150,
        dueDate: new Date('2024-12-10'),
        category: 'financial',
        history: [{ id: 'history_item_real_estate_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_tax_1',
        content: 'Tax optimization planning',
        description: 'Meet with accountant for year-end tax strategies',
        priority: 7,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 90,
        dueDate: new Date('2024-12-15'),
        category: 'financial',
        history: [{ id: 'history_item_tax_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_net_worth_1',
        content: 'Calculate net worth progress',
        description: 'Update net worth tracking spreadsheet',
        priority: 8,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 30,
        dueDate: new Date('2024-12-20'),
        category: 'financial',
        history: [{ id: 'history_item_net_worth_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  },

  // SaaS Development Tasks
  {
    id: 'list_saas_mvp',
    title: '💻 SaaS MVP Development',
    description: 'Core features for developer productivity tool',
    status: 'complete',
    priority: 3,
    goalId: 'goal_side_income',
    deadline: new Date('2025-03-31'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 120,
    actualHours: 28,
    items: [
      {
        id: 'item_saas_1',
        content: 'Market research and validation',
        description: 'Survey 50 developers about pain points',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-10'),
        estimatedMinutes: 600,
        actualMinutes: 720,
        history: [{ id: 'history_item_saas_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_2',
        content: 'Define MVP feature set',
        description: 'Core features based on research',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-12'),
        estimatedMinutes: 240,
        actualMinutes: 180,
        history: [{ id: 'history_item_saas_2_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_3',
        content: 'Design system and UI mockups',
        description: 'Figma designs for all screens',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-18'),
        estimatedMinutes: 480,
        actualMinutes: 600,
        history: [{ id: 'history_item_saas_3_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_4',
        content: 'Set up project infrastructure',
        description: 'Next.js, PostgreSQL, Prisma, Auth',
        priority: 4,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 360,
        dueDate: new Date('2024-11-30'),
        history: [{ id: 'history_item_saas_4_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_5',
        content: 'Implement authentication',
        description: 'OAuth with GitHub/Google',
        priority: 5,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 480,
        dueDate: new Date('2024-12-10'),
        history: [{ id: 'history_item_saas_5_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_6',
        content: 'Build core dashboard',
        description: 'Main app interface and navigation',
        priority: 6,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 720,
        dueDate: new Date('2024-12-20'),
        history: [{ id: 'history_item_saas_6_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_7',
        content: 'Implement key feature #1',
        description: 'Code snippet manager',
        priority: 7,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 960,
        dueDate: new Date('2025-01-10'),
        history: [{ id: 'history_item_saas_7_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_8',
        content: 'Implement key feature #2',
        description: 'Project template generator',
        priority: 8,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 960,
        dueDate: new Date('2025-01-25'),
        history: [{ id: 'history_item_saas_8_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_9',
        content: 'Integrate Stripe payments',
        description: 'Subscription billing setup',
        priority: 9,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 480,
        dueDate: new Date('2025-02-10'),
        history: [{ id: 'history_item_saas_9_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_10',
        content: 'Write documentation',
        description: 'User guide and API docs',
        priority: 10,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 360,
        dueDate: new Date('2025-02-20'),
        history: [{ id: 'history_item_saas_10_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_11',
        content: 'Beta testing setup',
        description: 'Deploy to staging, invite testers',
        priority: 11,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 240,
        dueDate: new Date('2025-03-01'),
        history: [{ id: 'history_item_saas_11_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_saas_12',
        content: 'Launch preparation',
        description: 'Landing page, ProductHunt, marketing',
        priority: 12,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 480,
        dueDate: new Date('2025-03-20'),
        history: [{ id: 'history_item_saas_12_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  },
  // Second list for passive income goal
  {
    id: 'list_dividend_portfolio',
    title: '📈 Dividend Portfolio Building',
    description: 'Research and build dividend growth stock portfolio',
    status: 'complete',
    priority: 3,
    goalId: 'goal_passive_income',
    deadline: new Date('2025-06-30'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 15,
    actualHours: 4,
    items: [
      {
        id: 'item_div_1',
        content: 'Research dividend aristocrats',
        description: 'Companies with 25+ years of dividend growth',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 120,
        dueDate: new Date('2024-12-01'),
        category: 'financial',
        history: [{ id: 'history_item_div_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_div_2',
        content: 'Analyze REIT options',
        description: 'Real estate investment trusts for diversification',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 90,
        dueDate: new Date('2024-12-10'),
        category: 'financial',
        history: [{ id: 'history_item_div_2_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_div_3',
        content: 'Set up DRIP for all holdings',
        description: 'Dividend reinvestment plans',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 30,
        dueDate: new Date('2024-12-15'),
        category: 'financial',
        history: [{ id: 'history_item_div_3_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  },
  // Third list for passive income goal
  {
    id: 'list_digital_products',
    title: '💻 Digital Product Creation',
    description: 'Create digital products for passive income',
    status: 'complete',
    priority: 4,
    goalId: 'goal_passive_income',
    deadline: new Date('2026-06-30'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 40,
    items: [
      {
        id: 'item_digital_1',
        content: 'Identify course topic based on expertise',
        description: 'Survey audience for pain points',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 180,
        dueDate: new Date('2025-02-01'),
        category: 'financial',
        history: [{ id: 'history_item_digital_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_digital_2',
        content: 'Create course outline',
        description: 'Module structure and learning outcomes',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 240,
        dueDate: new Date('2025-03-01'),
        category: 'financial',
        history: [{ id: 'history_item_digital_2_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_digital_3',
        content: 'Record video content',
        description: 'High-quality video lessons',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 600,
        dueDate: new Date('2025-05-01'),
        category: 'financial',
        history: [{ id: 'history_item_digital_3_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  },
  // First list for emergency fund goal
  {
    id: 'list_budget_optimization',
    title: '📊 Budget Optimization',
    description: 'Reduce expenses and increase savings rate',
    status: 'complete',
    priority: 2,
    goalId: 'goal_emergency_fund',
    deadline: new Date('2024-12-31'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 10,
    actualHours: 3,
    items: [
      {
        id: 'item_budget_audit',
        content: 'Complete spending audit for last 3 months',
        description: 'Categorize all expenses',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 120,
        dueDate: new Date('2024-11-15'),
        category: 'financial',
        history: [{ id: 'history_item_budget_audit_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_budget_subscriptions',
        content: 'Cancel unused subscriptions',
        description: 'Review and eliminate unnecessary recurring costs',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 60,
        dueDate: new Date('2024-11-20'),
        category: 'financial',
        history: [{ id: 'history_item_budget_subscriptions_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_budget_negotiate',
        content: 'Negotiate bills (insurance, phone, internet)',
        description: 'Call providers for better rates',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 90,
        dueDate: new Date('2024-11-25'),
        category: 'financial',
        history: [{ id: 'history_item_budget_negotiate_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  },
  // Second list for emergency fund goal
  {
    id: 'list_savings_automation',
    title: '🔄 Savings Automation',
    description: 'Set up automatic savings transfers',
    status: 'complete',
    priority: 2,
    goalId: 'goal_emergency_fund',
    deadline: new Date('2025-01-31'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 5,
    items: [
      {
        id: 'item_auto_1',
        content: 'Open high-yield savings account',
        description: 'Research best rates (4%+ APY)',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 60,
        dueDate: new Date('2024-12-01'),
        category: 'financial',
        history: [{ id: 'history_item_auto_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_auto_2',
        content: 'Set up automatic transfer $2000/month',
        description: 'Schedule recurring transfer on payday',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 15,
        dueDate: new Date('2024-12-05'),
        category: 'financial',
        history: [{ id: 'history_item_auto_2_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_auto_3',
        content: 'Create savings milestones tracker',
        description: 'Spreadsheet to track progress to $60k',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 30,
        dueDate: new Date('2024-12-10'),
        category: 'financial',
        history: [{ id: 'history_item_auto_3_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  },
  // Third list for emergency fund goal
  {
    id: 'list_income_boost',
    title: '💵 Income Boost Strategies',
    description: 'Short-term income increase for faster savings',
    status: 'complete',
    priority: 3,
    goalId: 'goal_emergency_fund',
    deadline: new Date('2025-03-31'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 8,
    items: [
      {
        id: 'item_income_1',
        content: 'Sell unused items (electronics, clothes)',
        description: 'List on eBay, Facebook Marketplace',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 180,
        dueDate: new Date('2024-12-15'),
        category: 'financial',
        history: [{ id: 'history_item_income_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_income_2',
        content: 'Research freelance opportunities',
        description: 'Use existing skills for side gigs',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 120,
        dueDate: new Date('2025-01-15'),
        category: 'financial',
        history: [{ id: 'history_item_income_2_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_income_3',
        content: 'Negotiate salary raise',
        description: 'Prepare case with accomplishments',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 180,
        dueDate: new Date('2025-02-15'),
        category: 'financial',
        history: [{ id: 'history_item_income_3_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  },
  // Second list for side income goal
  {
    id: 'list_saas_marketing',
    title: '📣 SaaS Marketing Strategy',
    description: 'Pre-launch marketing and audience building',
    status: 'complete',
    priority: 4,
    goalId: 'goal_side_income',
    deadline: new Date('2025-03-31'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 30,
    items: [
      {
        id: 'item_market_1',
        content: 'Build email waitlist landing page',
        description: 'Capture early adopters',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 240,
        dueDate: new Date('2024-12-15'),
        category: 'financial',
        history: [{ id: 'history_item_market_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_market_2',
        content: 'Create content marketing plan',
        description: 'Blog posts, Twitter threads, dev.to',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 180,
        dueDate: new Date('2025-01-01'),
        category: 'financial',
        history: [{ id: 'history_item_market_2_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_market_3',
        content: 'Build in public on Twitter',
        description: 'Share progress weekly',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 60,
        dueDate: new Date('2025-01-15'),
        category: 'financial',
        history: [{ id: 'history_item_market_3_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  },
  // Third list for side income goal
  {
    id: 'list_saas_operations',
    title: '⚙️ SaaS Operations Setup',
    description: 'Business infrastructure and legal setup',
    status: 'complete',
    priority: 5,
    goalId: 'goal_side_income',
    deadline: new Date('2025-02-28'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-20'),
    estimatedHours: 15,
    items: [
      {
        id: 'item_ops_1',
        content: 'Register LLC',
        description: 'Business entity formation',
        priority: 1,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 120,
        dueDate: new Date('2024-12-20'),
        category: 'financial',
        history: [{ id: 'history_item_ops_1_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_ops_2',
        content: 'Set up business bank account',
        description: 'Separate finances from personal',
        priority: 2,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 60,
        dueDate: new Date('2025-01-05'),
        category: 'financial',
        history: [{ id: 'history_item_ops_2_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_ops_3',
        content: 'Create terms of service and privacy policy',
        description: 'Legal documents for SaaS',
        priority: 3,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 180,
        dueDate: new Date('2025-01-20'),
        category: 'financial',
        history: [{ id: 'history_item_ops_3_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      },
      {
        id: 'item_ops_4',
        content: 'Set up accounting system',
        description: 'QuickBooks or similar for tracking',
        priority: 4,
        createdAt: new Date('2024-11-01'),
        updatedAt: new Date('2024-11-01'),
        estimatedMinutes: 90,
        dueDate: new Date('2025-02-01'),
        category: 'financial',
        history: [{ id: 'history_item_ops_4_completed', action: 'completed', timestamp: new Date('2024-11-20'), note: 'Task completed' }]
      }
    ]
  }
];